import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { profileApi } from "./profilesApi";
import { useAuth, TOKEN_KEY, ACTIVE_SCHOOL_KEY } from "./AuthContext";

const ACTIVE_KEY = ACTIVE_SCHOOL_KEY;

const ActiveSchoolContext = createContext({
  profiles: [],
  activeId: null,
  active: null,
  loading: false,
  setActiveId: () => {},
  refresh: async () => {},
});

export function ActiveSchoolProvider({ children }) {
  const { token, ready } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveIdState] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(ACTIVE_KEY) || null;
  });
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  const refresh = useCallback(async () => {
    // Skip silently when no auth token — profileApi requires auth now.
    if (typeof window !== "undefined" && !window.localStorage.getItem(TOKEN_KEY)) {
      setProfiles([]);
      setLoading(false);
      setHasFetched(true);
      return;
    }
    try {
      setLoading(true);
      const data = await profileApi.list();
      setProfiles(data);
      // If active id no longer exists in list, clear it
      if (data.length === 0) {
        setActiveIdState(null);
        window.localStorage.removeItem(ACTIVE_KEY);
      } else {
        const stillExists = activeId && data.some((p) => p.id === activeId);
        if (!stillExists) {
          // Pick first
          const first = data[0].id;
          setActiveIdState(first);
          window.localStorage.setItem(ACTIVE_KEY, first);
        }
      }
    } catch (err) {
      // Swallow 401s during login flow — provider will re-fetch after sign-in.
      setProfiles([]);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch profiles whenever auth state flips (login / logout / token refresh
  // complete). `ready` ensures we don't double-fetch before the initial
  // /auth/me round-trip resolves.
  useEffect(() => {
    if (!ready) return;
    refresh();
  }, [ready, token, refresh]);

  const setActiveId = (id) => {
    setActiveIdState(id);
    if (id) window.localStorage.setItem(ACTIVE_KEY, id);
    else window.localStorage.removeItem(ACTIVE_KEY);
  };

  const active = profiles.find((p) => p.id === activeId) || null;

  return (
    <ActiveSchoolContext.Provider value={{ profiles, activeId, active, loading, hasFetched, setActiveId, refresh }}>
      {children}
    </ActiveSchoolContext.Provider>
  );
}

export function useActiveSchool() {
  return useContext(ActiveSchoolContext);
}
